import Stripe from "stripe";
import { ExerciseCategory, InterfaceLocale, Price, UserSettings } from "types";

import { Database } from "@/types_db";

import { DAILY_EXERCISES_LIMIT } from "./constants";

/**
 * Get the current local time as a timestamp string
 * @returns {string} a string containing the current timestamp in the format YYYY-MM-DD HH:MM:SS
 */
export const getLocalTimestamp = () => {
    const NOW = new Date();

    return (
        NOW.getFullYear() +
        "-" +
        ("0" + (NOW.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + NOW.getDate()).slice(-2) +
        " " +
        ("0" + NOW.getHours()).slice(-2) +
        ":" +
        ("0" + NOW.getMinutes()).slice(-2) +
        ":" +
        ("0" + NOW.getSeconds()).slice(-2)
    );
};

// a getter function to get the user settings in the local storage, containing an object with user settings
export const getLocalUserSettings = (): UserSettings => {
    if (typeof window === "undefined") return {} as any;
    const userSettings = localStorage.getItem("userSettings");
    return userSettings ? JSON.parse(userSettings) : {};
};

// a setter function to set the user settings in the local storage, containing an object with user settings
export const setLocalUserSettings = (userSettings: UserSettings) => {
    localStorage.setItem("userSettings", JSON.stringify(userSettings));
};

export const deleteLocalUserSettings = () => {
    localStorage.clear();
    sessionStorage.clear();
};

// a getter function to get the string data from key `exerciseText` in the local storage, containing a string
export const getExerciseText = (): string => {
    if (typeof window === "undefined") return "";
    const exerciseText = localStorage.getItem("exerciseText");
    return exerciseText ? JSON.parse(exerciseText) : "";
};

// a setter function to set the string data to key `exerciseText` in the local storage, containing a string
export const setExerciseText = (exerciseText: string) => {
    localStorage.setItem("exerciseText", JSON.stringify(exerciseText));
};

export const getURL = () => {
    let url =
        process?.env?.NEXT_PUBLIC_SITE_URL ?? // Set this to your site URL in production env.
        process?.env?.NEXT_PUBLIC_VERCEL_URL ?? // Automatically set by Vercel.
        "http://localhost:3000/";
    // Make sure to include `https://` when not localhost.
    url = url.includes("http") ? url : `https://${url}`;
    // Make sure to including trailing `/`.
    url = url.charAt(url.length - 1) === "/" ? url : `${url}/`;
    return url;
};

// Set the value of the `NEXT_LOCALE` cookie to the user's locale
export function setLocaleCookie(locale: InterfaceLocale) {
    document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000`;
}

export function response(data: object, status: number) {
    return new Response(JSON.stringify(data), { status });
}

export function responseStatus422(message: string) {
    return Response.json({ error: { message } }, { status: 422 });
}

export const postData = async ({
    url,
    data
}: {
    url: string;
    data?: { price: Price };
}) => {
    const res: Response = await fetch(url, {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
        credentials: "same-origin",
        body: JSON.stringify(data)
    });

    if (!res.ok) {
        console.log("Error in postData", { url, data, res });

        throw Error(res.statusText);
    }

    return res.json();
};

export const toDateTime = (secs: number) => {
    const t = new Date(+0); // Unix epoch start.
    t.setSeconds(secs);
    return t;
};

export const isDev = process.env.NODE_ENV === "development";

export function debug(describer: string, ...values: any[]) {
    if (!isDev) return;

    const isNode =
        typeof process !== "undefined" &&
        process.versions !== null &&
        process.versions.node !== null;

    if (isNode) {
        // Node.js: Using ANSI escape codes for styling
        const ansiStyling = `\x1b[45m\x1b[37m\x1b[1m${describer}\x1b[0m`;
        console.log(ansiStyling, ...values);
    } else {
        // Browser: Using CSS styling
        console.log(
            `%c${describer}`,
            "font-weight: bold; background-color: magenta; color: white;",
            ...values
        );
    }
}

export function getExercisesLeftToday(
    count: Record<ExerciseCategory, { goal: number | null; done: number }>
) {
    const total = Object.values(count).reduce(
        (acc, curr) => acc + curr.done,
        0
    );

    return DAILY_EXERCISES_LIMIT - total;
}

export function buildSubscriptionData({
    subscription,
    uuid
}: {
    subscription: Stripe.Response<Stripe.Subscription>;
    uuid: string;
}): Database["public"]["Tables"]["subscriptions"]["Insert"] {
    return {
        id: subscription.id,
        user_id: uuid,
        metadata: subscription.metadata,
        status: subscription.status,
        price_id: subscription.items.data[0].price.id,
        //TODO check quantity on subscription
        // @ts-ignore
        quantity: subscription.quantity,
        cancel_at_period_end: subscription.cancel_at_period_end,
        cancel_at: subscription.cancel_at
            ? toDateTime(subscription.cancel_at).toISOString()
            : null,
        canceled_at: subscription.canceled_at
            ? toDateTime(subscription.canceled_at).toISOString()
            : null,
        current_period_start: toDateTime(
            subscription.current_period_start
        ).toISOString(),
        current_period_end: toDateTime(
            subscription.current_period_end
        ).toISOString(),
        created: toDateTime(subscription.created).toISOString(),
        ended_at: subscription.ended_at
            ? toDateTime(subscription.ended_at).toISOString()
            : null,
        trial_start: subscription.trial_start
            ? toDateTime(subscription.trial_start).toISOString()
            : null,
        trial_end: subscription.trial_end
            ? toDateTime(subscription.trial_end).toISOString()
            : null
    };
}

export function buildLifetimeData({
    paymentIntent,
    uuid,
    priceId
}: {
    paymentIntent: Stripe.Response<Stripe.PaymentIntent>;
    uuid: string;
    priceId?: string;
}): Database["public"]["Tables"]["subscriptions"]["Insert"] {
    return {
        id: paymentIntent.id,
        user_id: uuid,
        metadata: paymentIntent.metadata,
        status: paymentIntent.status,
        price_id: priceId || null,
        //TODO check quantity on subscription
        // @ts-ignore
        quantity: paymentIntent.quantity,
        cancel_at_period_end: null,
        cancel_at: null,
        canceled_at: paymentIntent.canceled_at
            ? toDateTime(paymentIntent.canceled_at).toISOString()
            : null,
        created: toDateTime(paymentIntent.created).toISOString(),
        ended_at: null,
        trial_start: null,
        trial_end: null
    };
}
